import React, { FC } from "react";
import { AssignedTo, CompanyAssignmentConfigDto } from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import { useEntityAssignmentStore } from "../../AssignmentSidePanel.store";
import AssignmentDropdown from "../../../AssignmentDropdown";

import { Table, TableColumn } from "@components/common";
import { useGetAssignableUsers } from "@api";
import { useUserStore } from "@store/user";

interface EntityAssignmentTable {
  isLoading?: boolean;
}

const EntityAssignmentTable: FC<EntityAssignmentTable> = ({ isLoading }) => {
  const { t } = useTranslation();
  const { user } = useUserStore();
  const {
    selectedEntities,
    entitiesAssignments,
    setSelectedEntities,
    setEntitiesAssignments,
  } = useEntityAssignmentStore();

  const { assignableUsers } = useGetAssignableUsers(
    user,
    {
      companyIds: isEmpty(selectedEntities)
        ? entitiesAssignments.map((e) => e.id)
        : selectedEntities,
    },
    true,
  );

  const handleSelectionChange = (selectedEntities: string[]): void => {
    setSelectedEntities(selectedEntities);
  };

  const handleAssignmentOpen = (
    companyAssignment: CompanyAssignmentConfigDto,
  ): void => {
    if (isEmpty(selectedEntities)) {
      setSelectedEntities([companyAssignment.id]);
    } else if (!selectedEntities.includes(companyAssignment.id)) {
      setSelectedEntities([companyAssignment.id]);
    }
  };

  const handleAssignmentChange = (
    item: {
      type: "general" | "user" | "users";
      value: string | AssignedTo | string[];
    },
    companyAssignment: CompanyAssignmentConfigDto,
  ): void => {
    // Update the assignments in the store
    const updatedAssignments = entitiesAssignments.map((assignment) => {
      const shouldUpdate = isEmpty(selectedEntities)
        ? assignment.id === companyAssignment.id
        : selectedEntities.includes(assignment.id);

      if (!shouldUpdate) {
        return assignment;
      }

      if (item.type === "general") {
        return {
          ...assignment,
          assignedTo: item.value as AssignedTo,
          assignedUsers: [],
        };
      } else if (item.type === "users") {
        const userIds = item.value as string[];

        return {
          ...assignment,
          assignedTo:
            userIds.length > 0 ? AssignedTo.UserSpecific : assignment.assignedTo,
          assignedUsers: userIds.map((id) => ({
            id,
            fullName: assignableUsers?.find((u) => u.id === id)?.fullName ?? "",
            isCommon: false,
          })),
        };
      }

      return assignment;
    });

    setEntitiesAssignments(updatedAssignments);
  };

  const columns: TableColumn<CompanyAssignmentConfigDto>[] = [
    {
      title: t("questionsAssignment.assignmentSidePanel.entity"),
      key: "companyName",
      dataKey: "name",
    },
    {
      title: t("questionsAssignment.assignmentSidePanel.assignedTo"),
      key: "assignedTo",
      renderCellContent: (companyAssignment) => (
        <AssignmentDropdown
          assignment={{
            assignedTo: companyAssignment.assignedTo,
            assignedUsers: companyAssignment.assignedUsers,
          }}
          handleChange={(item) => handleAssignmentChange(item, companyAssignment)}
          onOpen={() => handleAssignmentOpen(companyAssignment)}
          usersSelectionEnabled
          companyIds={
            isEmpty(selectedEntities)
              ? entitiesAssignments.map((e) => e.id)
              : selectedEntities
          }
        />
      ),
      tableCellProps: {
        width: 50,
        align: "center",
      },
    },
  ];

  return (
    <Table<CompanyAssignmentConfigDto>
      columns={columns}
      data={entitiesAssignments}
      isLoading={isLoading}
      // TODO: enable row selection when we solve the issue of user assignments across multiple entities
      // enableRowSelection
      onSelectionChange={handleSelectionChange}
      selectedRowIds={selectedEntities}
    />
  );
};

export default EntityAssignmentTable;
